import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, ShieldCheck, Calendar, Mail, Inbox, Loader2, CheckCircle2, XCircle, Clock, Trash2, Image as ImageIcon, Upload, Handshake, Star, FileDown, Download } from "lucide-react";
import { api, formatApiError, SITE } from "../lib/api";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS = ["L", "M", "M", "J", "V", "S", "D"];
const fmt = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("ap_token"));
  return token ? <Dashboard onLogout={() => { localStorage.removeItem("ap_token"); setToken(null); }} /> : <Login onLogin={(t) => { localStorage.setItem("ap_token", t); setToken(t); }} />;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      onLogin(res.data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-12 h-12 rounded-full bg-white p-1"><img src={SITE.logo} alt="" className="w-full h-full object-contain rounded-full" /></div>
          <div>
            <div className="font-display text-2xl">ANTON PEARS</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Espace Admin</div>
          </div>
        </Link>

        <div className="glass-strong rounded-2xl p-8">
          <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6] flex items-center gap-2"><ShieldCheck size={14} /> Connexion</div>
          <h1 className="font-display text-3xl mt-3">Tableau de bord</h1>
          <p className="text-sm text-zinc-400 mt-2">Gestion des disponibilités & demandes.</p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none" data-testid="admin-login-email" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Mot de passe</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none" data-testid="admin-login-password" />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="admin-login-error">{error}</div>}
            <button type="submit" disabled={loading} className="btn-neon w-full justify-center disabled:opacity-50" data-testid="admin-login-submit">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Connexion…</> : "Se connecter"}
            </button>
          </form>
        </div>

        <Link to="/" className="block text-center text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-[#3B82F6] mt-6">← Retour au site</Link>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("calendar");
  const [me, setMe] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {
      onLogout();
      navigate("/admin");
    });
  }, [onLogout, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="glass-strong border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white p-1"><img src={SITE.logo} alt="" className="w-full h-full object-contain rounded-full" /></div>
            <div>
              <div className="font-display text-lg leading-none">ANTON PEARS</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            {me && <div className="hidden md:block text-xs text-zinc-400">{me.email}</div>}
            <button onClick={onLogout} className="text-xs uppercase tracking-[0.3em] text-zinc-300 hover:text-[#3B82F6] inline-flex items-center gap-2" data-testid="admin-logout">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-2 -mb-px overflow-x-auto">
          <Tab id="calendar" active={tab === "calendar"} onClick={() => setTab("calendar")} icon={Calendar}>Calendrier</Tab>
          <Tab id="contacts" active={tab === "contacts"} onClick={() => setTab("contacts")} icon={Mail}>Devis</Tab>
          <Tab id="bookings" active={tab === "bookings"} onClick={() => setTab("bookings")} icon={Inbox}>Réservations</Tab>
          <Tab id="gallery" active={tab === "gallery"} onClick={() => setTab("gallery")} icon={ImageIcon}>Galerie</Tab>
          <Tab id="partners" active={tab === "partners"} onClick={() => setTab("partners")} icon={Handshake}>Partenaires</Tab>
          <Tab id="reviews" active={tab === "reviews"} onClick={() => setTab("reviews")} icon={Star}>Avis</Tab>
          <Tab id="reports" active={tab === "reports"} onClick={() => setTab("reports")} icon={FileDown}>Rapports</Tab>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {tab === "calendar" && <CalendarManager />}
        {tab === "contacts" && <List endpoint="/admin/contacts" type="contact" />}
        {tab === "bookings" && <List endpoint="/admin/bookings" type="booking" />}
        {tab === "gallery" && <GalleryManager />}
        {tab === "partners" && <PartnersManager />}
        {tab === "reviews" && <ReviewsManager />}
        {tab === "reports" && <ReportsManager />}
      </main>
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, children, id }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 text-xs uppercase tracking-[0.3em] inline-flex items-center gap-2 border-b-2 transition-colors ${active ? "border-[#3B82F6] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
      data-testid={`admin-tab-${id}`}
    >
      <Icon size={14} /> {children}
    </button>
  );
}

function CalendarManager() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [data, setData] = useState({});
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    const res = await api.get("/availability");
    const map = {};
    (res.data || []).forEach((d) => { map[d.date] = d; });
    setData(map);
  };
  useEffect(() => { load(); }, []);

  // Pré-remplir la note quand on sélectionne une date déjà enregistrée
  useEffect(() => {
    if (selected && data[selected]) {
      setNote(data[selected].note || "");
    } else {
      setNote("");
    }
  }, [selected, data]);

  const setStatus = async (status) => {
    if (!selected) return;
    await api.post("/availability", { date: selected, status, note });
    await load();
  };
  const remove = async () => {
    if (!selected) return;
    await api.delete(`/availability/${selected}`);
    await load();
    setNote("");
  };

  const days = (() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const start = (first.getDay() + 6) % 7;
    const total = last.getDate();
    const cells = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  })();

  const goPrev = () => { if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1); };
  const goNext = () => { if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1); };

  const cellClass = (d) => {
    if (!d) return "invisible";
    const ds = fmt(year, month, d);
    let cls = "day-cell";
    const status = data[ds]?.status;
    if (status === "available") cls += " available";
    if (status === "booked") cls += " booked";
    if (status === "option") cls += " option";
    if (selected === ds) cls += " selected";
    return cls;
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 glass-strong rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={goPrev} className="w-10 h-10 rounded-full border border-white/10 hover:border-[#3B82F6]" data-testid="admin-cal-prev">‹</button>
          <div className="font-display text-2xl">{MONTHS[month]} {year}</div>
          <button onClick={goNext} className="w-10 h-10 rounded-full border border-white/10 hover:border-[#3B82F6]" data-testid="admin-cal-next">›</button>
        </div>
        <div className="grid grid-cols-7 gap-2 mb-2">
          {DAYS.map((d, i) => <div key={i} className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-500">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {days.map((d, i) => (
            <div
              key={i}
              className={cellClass(d)}
              onClick={() => d && setSelected(fmt(year, month, d))}
              data-testid={d ? `admin-day-${fmt(year, month, d)}` : undefined}
            >
              {d}
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5 glass-strong rounded-2xl p-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Date sélectionnée</div>
        <div className="font-display text-2xl mt-1">{selected || "Aucune"}</div>
        {selected && (
          <>
            {(() => {
              const cur = data[selected];
              const statusMap = {
                available: { label: "Disponible", color: "#22c55e", bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.4)" },
                option: { label: "Option", color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)" },
                booked: { label: "Réservé", color: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.4)" },
              };
              const s = cur ? statusMap[cur.status] : null;
              return (
                <div className="mt-4 p-4 rounded-xl border border-white/10 bg-[#0a0a0a]" data-testid="admin-day-current">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Statut actuel</div>
                  {s ? (
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.2em]"
                      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }} />
                      {s.label}
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-500">Aucun statut défini</div>
                  )}
                  {cur?.note && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Note enregistrée</div>
                      <div className="text-sm text-zinc-200 whitespace-pre-wrap">{cur.note}</div>
                    </div>
                  )}
                  {cur?.updated_at && (
                    <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                      Mis à jour le {new Date(cur.updated_at).toLocaleString("fr-FR")}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="mt-5">
              <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Note (optionnelle)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none" placeholder="Ex: Mariage Dupont, lieu, contact…" data-testid="admin-day-note" />
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2">
              <button onClick={() => setStatus("available")} className="px-3 py-3 rounded-xl border border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5" data-testid="admin-set-available">
                <CheckCircle2 size={14} /> Dispo
              </button>
              <button onClick={() => setStatus("option")} className="px-3 py-3 rounded-xl border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5" data-testid="admin-set-option">
                <Clock size={14} /> Option
              </button>
              <button onClick={() => setStatus("booked")} className="px-3 py-3 rounded-xl border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5" data-testid="admin-set-booked">
                <XCircle size={14} /> Réservé
              </button>
            </div>
            <button onClick={remove} className="mt-3 w-full py-3 rounded-xl border border-white/10 text-zinc-400 hover:border-red-500/40 hover:text-red-400 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2" data-testid="admin-clear-day">
              <Trash2 size={14} /> Effacer le statut
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function List({ endpoint, type }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(endpoint).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [endpoint]);

  const remove = async (id) => {
    const label = type === "contact" ? "cette demande de devis" : "cette réservation";
    if (!window.confirm(`Supprimer ${label} ?`)) return;
    const path = type === "contact" ? `/admin/contacts/${id}` : `/admin/bookings/${id}`;
    await api.delete(path);
    load();
  };

  if (loading) return <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>;
  if (!items.length) return <div className="text-zinc-500">Aucune entrée pour le moment.</div>;

  return (
    <div className="grid md:grid-cols-2 gap-5">
      {items.map((it) => (
        <div key={it.id} className="glass-strong rounded-2xl p-6 relative" data-testid={`admin-${type}-item-${it.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display text-xl">{it.name}</div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#3B82F6] mt-1">{it.event_type}</div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{new Date(it.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-zinc-300">
            <a href={`mailto:${it.email}`} className="hover:text-[#3B82F6] truncate">{it.email}</a>
            <a href={`tel:${it.phone}`} className="hover:text-[#3B82F6]">{it.phone}</a>
            {it.event_date && <div>Date : <span className="text-white">{it.event_date}</span></div>}
            {it.guest_count && <div>Pers. : <span className="text-white">{it.guest_count}</span></div>}
          </div>
          {it.message && <p className="text-sm text-zinc-300 mt-4 p-3 bg-[#0a0a0a] rounded-xl border border-white/5 whitespace-pre-wrap">{it.message}</p>}
          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="inline-flex px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.3em] bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20">{it.status}</div>
            <button
              onClick={() => remove(it.id)}
              className="w-9 h-9 rounded-full border border-white/10 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 flex items-center justify-center transition-colors"
              data-testid={`admin-${type}-delete-${it.id}`}
              aria-label="Supprimer"
              title="Supprimer"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


const GALLERY_MAX = 30;

function GalleryManager() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/gallery");
      setPhotos(res.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setError("");
    if (!f) { setFile(null); setPreview(null); return; }
    if (f.size > 5 * 1024 * 1024) {
      setError("Image trop volumineuse (5 Mo maximum).");
      setFile(null); setPreview(null);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) { setError("Sélectionnez d'abord une image."); return; }
    if (photos.length >= GALLERY_MAX) { setError(`Limite de ${GALLERY_MAX} photos atteinte.`); return; }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null); setPreview(null); setCaption("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette photo de la galerie ?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8">
      {/* Upload card */}
      <div className="glass-strong rounded-2xl p-6 md:p-8" data-testid="gallery-upload-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Ajouter une photo</div>
            <div className="font-display text-2xl mt-1">Galerie publique</div>
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">
            <span className={photos.length >= GALLERY_MAX ? "text-red-400" : "text-white"}>{photos.length}</span> / {GALLERY_MAX} photos
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-12 gap-5">
          <label className="md:col-span-5 cursor-pointer block">
            <div className="border-2 border-dashed border-white/10 hover:border-[#3B82F6]/60 rounded-2xl p-6 text-center transition-colors h-full flex flex-col items-center justify-center min-h-[180px]">
              {preview ? (
                <img src={preview} alt="aperçu" className="max-h-32 rounded-lg object-contain" />
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-center text-[#3B82F6] mb-3">
                    <Upload size={18} />
                  </div>
                  <div className="text-sm text-zinc-300">Cliquez pour choisir une image</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-2">JPEG · PNG · WEBP · 5 Mo max</div>
                </>
              )}
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={onFileChange} data-testid="gallery-file-input" />
          </label>

          <div className="md:col-span-7 flex flex-col gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Légende (optionnelle)</label>
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={160}
                placeholder="Ex: Festival - Cognac, juillet 2025"
                className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
                data-testid="gallery-caption-input"
              />
            </div>

            {error && <div className="text-sm text-red-400" data-testid="gallery-upload-error">{error}</div>}

            <div className="flex gap-3 mt-auto">
              <button
                onClick={upload}
                disabled={!file || uploading || photos.length >= GALLERY_MAX}
                className="btn-neon disabled:opacity-50"
                data-testid="gallery-upload-submit"
              >
                {uploading ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <><Upload size={16} /> Ajouter</>}
              </button>
              {file && (
                <button onClick={() => { setFile(null); setPreview(null); setCaption(""); }} className="btn-ghost" data-testid="gallery-upload-cancel">
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photos grid */}
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Photos publiées</div>
        {loading ? (
          <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>
        ) : photos.length === 0 ? (
          <div className="text-zinc-500">Aucune photo dans la galerie.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-admin-grid">
            {photos.map((p) => (
              <div key={p.id} className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-[#3B82F6]/50 transition-colors" data-testid={`gallery-photo-${p.id}`}>
                <img src={p.url} alt={p.caption} className="w-full h-48 object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                  {p.caption && <div className="text-xs text-zinc-200 line-clamp-1">{p.caption}</div>}
                </div>
                <button
                  onClick={() => remove(p.id)}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 border border-white/10 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-delete-${p.id}`}
                  aria-label="Supprimer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


const PARTNERS_MAX = 10;
const PARTNER_DESC_MAX = 100;

function PartnersManager() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logo1, setLogo1] = useState(null);
  const [logo2, setLogo2] = useState(null);
  const [preview1, setPreview1] = useState(null);
  const [preview2, setPreview2] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/partners");
      setPartners(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pickFile = (which) => (e) => {
    const f = e.target.files?.[0];
    setError("");
    if (!f) {
      if (which === 1) { setLogo1(null); setPreview1(null); }
      else { setLogo2(null); setPreview2(null); }
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("Logo trop volumineux (5 Mo max).");
      return;
    }
    if (which === 1) { setLogo1(f); setPreview1(URL.createObjectURL(f)); }
    else { setLogo2(f); setPreview2(URL.createObjectURL(f)); }
  };

  const reset = () => {
    setName(""); setDescription("");
    setLogo1(null); setLogo2(null);
    setPreview1(null); setPreview2(null);
  };

  const submit = async () => {
    if (!name.trim()) { setError("Le nom est requis."); return; }
    if (!logo1) { setError("Au moins un logo est requis."); return; }
    if (description.length > PARTNER_DESC_MAX) { setError(`Description: ${PARTNER_DESC_MAX} caractères max.`); return; }
    if (partners.length >= PARTNERS_MAX) { setError(`Limite de ${PARTNERS_MAX} partenaires atteinte.`); return; }

    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("description", description);
      fd.append("logo1", logo1);
      if (logo2) fd.append("logo2", logo2);
      await api.post("/admin/partners", fd, { headers: { "Content-Type": "multipart/form-data" } });
      reset();
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce partenaire ?")) return;
    try {
      await api.delete(`/admin/partners/${id}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8">
      <div className="glass-strong rounded-2xl p-6 md:p-8" data-testid="partners-upload-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Ajouter un partenaire</div>
            <div className="font-display text-2xl mt-1">Page Partenaires</div>
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">
            <span className={partners.length >= PARTNERS_MAX ? "text-red-400" : "text-white"}>{partners.length}</span> / {PARTNERS_MAX}
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Nom du partenaire</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Ex: Domaine du Château"
              className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
              data-testid="partner-name-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 flex justify-between">
              <span>Texte (100 caractères max)</span>
              <span className={description.length > PARTNER_DESC_MAX ? "text-red-400" : "text-zinc-500"}>{description.length}/{PARTNER_DESC_MAX}</span>
            </label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, PARTNER_DESC_MAX))}
              placeholder="Courte description / spécialité"
              className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
              data-testid="partner-description-input"
            />
          </div>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <LogoSlot label="Logo 1 (requis)" preview={preview1} onChange={pickFile(1)} testid="partner-logo1-input" />
          <LogoSlot label="Logo 2 (optionnel)" preview={preview2} onChange={pickFile(2)} testid="partner-logo2-input" />
        </div>

        {error && <div className="mt-4 text-sm text-red-400" data-testid="partner-upload-error">{error}</div>}

        <div className="mt-6 flex gap-3">
          <button
            onClick={submit}
            disabled={submitting || partners.length >= PARTNERS_MAX}
            className="btn-neon disabled:opacity-50"
            data-testid="partner-submit"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <><Upload size={16} /> Ajouter le partenaire</>}
          </button>
          {(name || description || logo1 || logo2) && (
            <button onClick={reset} className="btn-ghost" data-testid="partner-reset">Annuler</button>
          )}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Partenaires publiés</div>
        {loading ? (
          <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>
        ) : partners.length === 0 ? (
          <div className="text-zinc-500">Aucun partenaire pour le moment.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="partners-admin-list">
            {partners.map((p) => (
              <div key={p.id} className="glass-strong rounded-2xl p-6 relative" data-testid={`partner-card-admin-${p.id}`}>
                <div className="flex gap-3 mb-4">
                  {p.logos?.map((logo, idx) => (
                    <div key={idx} className="w-20 h-20 rounded-xl bg-white/95 p-2 flex items-center justify-center">
                      <img src={logo} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                  ))}
                </div>
                <div className="font-display text-xl">{p.name}</div>
                {p.description && <p className="text-sm text-zinc-400 mt-2">{p.description}</p>}
                <button
                  onClick={() => remove(p.id)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 border border-white/10 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 flex items-center justify-center transition-colors"
                  data-testid={`partner-delete-${p.id}`}
                  aria-label="Supprimer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogoSlot({ label, preview, onChange, testid }) {
  return (
    <label className="cursor-pointer block">
      <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 mb-2">{label}</div>
      <div className="border-2 border-dashed border-white/10 hover:border-[#3B82F6]/60 rounded-xl p-4 text-center transition-colors min-h-[120px] flex items-center justify-center">
        {preview ? (
          <img src={preview} alt="aperçu" className="max-h-24 object-contain" />
        ) : (
          <div className="text-xs text-zinc-500 inline-flex items-center gap-2"><Upload size={14} /> Choisir un logo</div>
        )}
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={onChange} data-testid={testid} />
    </label>
  );
}

function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reviews");
      setReviews(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="glass-strong rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid="reviews-admin-header">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Modération</div>
          <div className="font-display text-2xl mt-1">Avis clients</div>
          <p className="text-sm text-zinc-500 mt-1">Les avis sont publiés automatiquement. Supprime ceux que tu ne veux plus afficher.</p>
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">
          <span className="text-white">{reviews.length}</span> avis publiés
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>
      ) : reviews.length === 0 ? (
        <div className="text-zinc-500">Aucun avis pour le moment.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="admin-reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="glass-strong rounded-2xl p-6 relative" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex gap-1 mb-2">
                    {Array.from({ length: r.rating || 0 }).map((_, i) => (
                      <Star key={i} size={14} className="fill-[#3B82F6] text-[#3B82F6]" />
                    ))}
                  </div>
                  <div className="font-display text-lg">{r.name}</div>
                  {r.role && <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">{r.role}</div>}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 shrink-0">{r.source === "client" ? "Client" : "Origine"}</div>
              </div>
              <p className="text-sm text-zinc-300 mt-4 leading-relaxed whitespace-pre-wrap">"{r.text}"</p>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-4">
                {new Date(r.created_at).toLocaleString("fr-FR")}
              </div>
              <button
                onClick={() => remove(r.id)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/70 border border-white/10 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 flex items-center justify-center transition-colors"
                data-testid={`admin-review-delete-${r.id}`}
                aria-label="Supprimer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const FR_MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function ReportsManager() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  const download = async () => {
    setDownloading(true);
    setError("");
    try {
      const res = await api.get(`/admin/reports/pdf?year=${year}&month=${month}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anton-pears-bilan-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const monthLabel = FR_MONTHS[month - 1];

  return (
    <div className="space-y-6" data-testid="reports-manager">
      <div className="glass-strong rounded-2xl p-6 md:p-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Rapports mensuels</div>
        <div className="font-display text-3xl mt-2">Télécharger le bilan PDF</div>
        <p className="text-sm text-zinc-400 mt-2 max-w-xl">
          Génère un PDF récapitulatif du mois choisi : demandes de devis, réservations et entrées planning. Idéal pour ta comptabilité et pour ton archivage.
        </p>

        <div className="mt-8 grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Mois</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
              data-testid="report-month-select"
            >
              {FR_MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Année</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
              data-testid="report-year-select"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <button
              onClick={download}
              disabled={downloading}
              className="btn-neon w-full justify-center disabled:opacity-50"
              data-testid="report-download-btn"
            >
              {downloading ? <><Loader2 size={16} className="animate-spin" /> Génération…</> : <><Download size={16} /> Télécharger PDF</>}
            </button>
          </div>
        </div>

        {error && <div className="text-sm text-red-400 mt-4" data-testid="report-error">{error}</div>}

        <div className="mt-8 p-5 bg-[#0a0a0a] border border-white/5 rounded-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Aperçu</div>
          <div className="font-display text-2xl mt-2">Bilan · {monthLabel} {year}</div>
          <p className="text-sm text-zinc-500 mt-1">Fichier généré : <span className="text-zinc-300">anton-pears-bilan-{year}-{String(month).padStart(2, "0")}.pdf</span></p>
        </div>
      </div>

      <div className="glass-strong rounded-2xl p-6 border-l-4 border-[#3B82F6]/60">
        <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Rappel automatique</div>
        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
          Chaque 1<sup>er</sup> du mois, un email est envoyé automatiquement pour te rappeler de télécharger le bilan du mois précédent.
        </p>
      </div>
    </div>
  );
}

